'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ArrowUpRight, Camera, Check, ChevronRight, HandHeart, Landmark, Plus, Quote, Send,
  ShieldCheck, Ticket, UserRound, Users, X, type LucideIcon,
} from 'lucide-react';

import { W, T, FONT, LAYOUT, SHADOW } from './tokens';
import { WebsiteHeader, WebsiteMark, primaryButton, secondaryButton } from './WebsiteHero';
import { HomeHero, HERO_BG } from './hero/HomeHero';
import { FEATURES } from './features';
import { SURFACES } from './surfaces';
import { ANIMS, MotionBand } from './animations';
import { POINT_ICONS } from './point-icons';
import { ContactForm } from './ContactForm';
import { useScrollGatedMotion } from './motion';
import { CHIPS_ON_TILE, EVENT_TYPES, type EventType } from './event-types';

/**
 * The Fitvent marketing homepage (design_handoff_fitvent_website_v8).
 *
 * The argument the page makes: every organiser gets the same CORE platform
 * (tickets, payments, waivers, comms, volunteers, payouts, athlete accounts)
 * and only the COMPETITION ENGINE changes by event type. Everything is
 * arranged to serve that, so resist adding sections that restate features.
 *
 * TWO DELIBERATE DEPARTURES FROM THE HANDOFF
 *
 * 1. Pricing says "4.5% plus standard payment processing", as the handoff
 *    does: payments are direct charges on the organiser's own Stripe account,
 *    where Stripe charges its processing fee to them and Fitvent takes 4.5%. The handoff flagged this as a commercial check;
 *    this is the answer.
 *
 * 2. Every product surface on this page is DRAWN, not screenshotted. The hero
 *    loop, the eight feature cards and the overlays are all components
 *    (hero/, surfaces.tsx). Captures clipped badly across card widths,
 *    went soft on retina, carried whatever was in the database the morning they
 *    were taken, and rot the moment the product moves. Photographs remain
 *    photographs: the event-type tiles and the organiser logos.
 */
export function WebsiteHome() {
  const [open, setOpen] = useState<(typeof FEATURES)[number] | null>(null);
  const [openType, setOpenType] = useState<EventType | null>(null);
  const quotesMobile = useIsMobile();
  useScrollGatedMotion();

  return (
    <div id="top" className="fvw-page" data-fv-gate style={{ background: W.page, fontFamily: FONT, color: W.ink }}>
      {/* data-fv-gate above is what arms the scroll gate in globals.css, and it
          has to be server-rendered: the animations are inline styles that start
          on first paint, so anything that only takes effect after hydration is
          already too late and produces exactly the restart glitch the gate
          exists to remove.

          Which leaves one hole. With scripting off nothing ever stamps
          data-fv-played, every block stays paused at the head of its keyframes,
          and since those start at opacity 0 the page would be largely blank.
          This releases the lot in that case. The :root prefix is doing real
          work: it outranks the pause rule, which is !important. */}
      <noscript>
        <style>{`:root [data-fv-gate] [data-fv-motion]:not([data-fv-played]) [style*="animation"] { animation-play-state: running !important; }`}</style>
      </noscript>
      {/* 1 + 2. Dark band: header, hero, logo strip. The hero owns its glows
          and starts them above itself, so the light washes up behind the
          header rather than stopping in a hard line under it. */}
      <div style={{ background: HERO_BG, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 2, maxWidth: LAYOUT.maxWidth, margin: '0 auto' }}>
          <WebsiteHeader />
        </div>
        <HomeHero />
        <div style={{ position: 'relative', maxWidth: LAYOUT.maxWidth, margin: '0 auto' }}>
          <LogoStrip />
        </div>
      </div>

      {/* 3. Feature grid */}
      <section id="platform" className="fvw-section" style={{ maxWidth: LAYOUT.maxWidth, margin: '0 auto', padding: LAYOUT.sectionPadding }}>
        <h2 style={{ ...T.h2, margin: '0 0 44px', maxWidth: 900 }} className="fvw-h2">
          One system for the entry, <span style={{ color: W.teal }}>the day</span> and the money.
        </h2>
        <div className="fvw-grid">
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} feature={f} onOpen={() => setOpen(f)} />
          ))}
        </div>
      </section>

      {/* 4 + 5. Core and competition engine */}
      <section id="eventtypes" className="fvw-section" style={{ maxWidth: LAYOUT.maxWidth, margin: '0 auto', padding: LAYOUT.sectionPadding }}>
        <h2 style={{ ...T.h2, margin: '0 0 20px', maxWidth: 900 }} className="fvw-h2">
          Every organiser gets the core. Only{' '}
          <span style={{ color: W.teal }}>the format and the scoring</span> change.
        </h2>
        <p style={{ ...T.body, color: W.body, maxWidth: 720, margin: '0 0 40px' }}>
          Tickets, payments, waivers, comms, volunteers and payouts work the same whatever you run.
          Only the way you decide who competes against whom changes.
        </p>
        <CorePanel />
        <EventTypeScroller onOpen={setOpenType} />
      </section>

      {/* 6. Testimonials */}
      <section style={{ background: W.obsidian }}>
        <div className="fvw-section" style={{ maxWidth: LAYOUT.maxWidth, margin: '0 auto', padding: LAYOUT.sectionPadding }}>
          {/* Each quote in its own card. Two floating blocks of text on a black
              ground ran together, and on a phone the carousel sliced the second
              one down the middle mid-word. Cards separate them at any width and
              read as deliberate rather than as something that did not fit. */}
          <div className="fvw-two-col" style={{ alignItems: 'stretch' }}>
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                style={{
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  background: 'rgba(244,250,248,0.035)',
                  border: '1px solid rgba(244,250,248,0.10)',
                  borderRadius: 16,
                  padding: quotesMobile ? '24px 22px' : '30px 30px 28px',
                }}
              >
                <Quote size={22} strokeWidth={2} color={W.ice} style={{ flex: 'none', marginBottom: 14, opacity: 0.9 }} />
                <blockquote
                  style={{
                    ...T.heroSub,
                    fontSize: quotesMobile ? 19 : 22,
                    color: 'rgba(244,250,248,0.74)',
                    margin: '0 0 22px',
                  }}
                >
                  “{t.quote.map((part, i) =>
                    typeof part === 'string' ? (
                      <span key={i}>{part}</span>
                    ) : (
                      <strong key={i} style={{ color: W.bone, fontWeight: 600 }}>{part.em}</strong>
                    ),
                  )}”
                </blockquote>
                <figcaption
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto',
                    paddingTop: 18, borderTop: '1px solid rgba(244,250,248,0.10)',
                  }}
                >
                  <Image
                    src={t.portrait}
                    alt=""
                    width={64}
                    height={64}
                    style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flex: 'none' }}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: W.bone }}>{t.name}</span>
                    <span style={{ display: 'block', fontSize: 15, color: W.dim }}>{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Pricing */}
      <section id="pricing" style={{ background: W.tint }}>
        <div className="fvw-section" style={{ maxWidth: LAYOUT.maxWidth, margin: '0 auto', padding: LAYOUT.sectionPadding }}>
          <h2 style={{ ...T.h2, margin: '0 0 40px' }} className="fvw-h2">
            No monthly fee. <span style={{ color: W.teal }}>You pay when you sell.</span>
          </h2>
          <div className="fvw-two-col">
            <div style={{ background: W.obsidian, borderRadius: 20, padding: 40, boxShadow: SHADOW.panel }}>
              <div style={{ fontSize: 72, fontWeight: 700, letterSpacing: '-0.05em', color: W.bone, lineHeight: 1 }}>
                4.5%
              </div>
              <p style={{ ...T.body, color: W.dim, margin: '10px 0 26px' }}>
                Per sale, plus standard payment processing. At least €1 a payment, and nothing when an entry is free.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 30px', display: 'grid', gap: 12 }}>
                {INCLUDED.map((item) => (
                  <li key={item} style={{ display: 'flex', gap: 10, fontSize: 17, color: W.bone }}>
                    <span aria-hidden style={{ color: W.ice }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="#walkthrough" style={primaryButton} className="fvw-cta">Get started</a>
            </div>

            <div style={{ background: '#FFFFFF', borderRadius: 20, padding: 40, border: `1px solid ${W.rule}` }}>
              <div style={{ ...T.h2, fontSize: 36, margin: 0 }}>Free events are free.</div>
              <p style={{ ...T.body, color: W.body, margin: '14px 0 26px' }}>
                List a free event and it costs nothing, to you or to your athletes.
              </p>
              <a href="#walkthrough" style={{ ...secondaryButton, color: W.ink, borderColor: W.borderStrong }} className="fvw-cta-secondary">
                Talk to us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Closing CTA and footer */}
      <footer id="walkthrough" style={{ background: W.obsidian }}>
        <div className="fvw-section" style={{ maxWidth: LAYOUT.maxWidth, margin: '0 auto', padding: LAYOUT.sectionPadding }}>
          <div className="fvw-two-col" style={{ alignItems: 'start' }}>
            <div>
              <WebsiteMark size={40} accent={W.ice} body={W.bone} />
              <h2 style={{ ...T.h2, fontSize: 48, color: W.bone, margin: '20px 0 16px' }} className="fvw-h2">
                Bring us your next event.
              </h2>
              <p style={{ ...T.body, color: W.dim, margin: 0, maxWidth: 460 }}>
                Fitvent is in private beta with a small group of organisers. Tell us what you run and
                we will show you the platform on your own event.
              </p>
            </div>
            <ContactForm />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 20,
              flexWrap: 'wrap',
              marginTop: 64,
              paddingTop: 26,
              borderTop: `1px solid ${W.line}`,
              fontSize: 16,
              color: '#7C8D88',
            }}
          >
            <span>One platform for every fitness event. Built in Ireland.</span>
            <span style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <a href="mailto:hello@fitvent.com" style={footerLink}>hello@fitvent.com</a>
              <a href="/privacy" style={footerLink}>Privacy</a>
              <a href="/terms" style={footerLink}>Terms</a>
              <span>1 Terenure Place, Terenure, Dublin, D6W FN23</span>
            </span>
          </div>
        </div>
      </footer>

      {open && <FeatureOverlay feature={open} onClose={() => setOpen(null)} onOpen={setOpen} />}
      {openType && <EventTypeOverlay type={openType} onClose={() => setOpenType(null)} />}
    </div>
  );
}

/**
 * Escape closes an overlay, and the page behind it stops scrolling while one is
 * open. Both overlays are click-outside-to-close, which is discoverable but not
 * announced; Escape is the one every keyboard user reaches for first, and
 * without the scroll lock the page slides around underneath on a trackpad.
 */
function useDismissable(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
}

/**
 * The full-screen overlay a card opens: four zones, per the handoff.
 *
 * Full screen rather than an in-place accordion, which the handoff is explicit
 * about and right about: an accordion inside a multi-column grid always leaves
 * a void beside the open card, and no card should change height because a
 * different one opened.
 *
 * THE OVERLAY MUST NOT REPEAT THE CLOSED CARD'S VISUAL. That was the state of
 * an earlier build and it made clicking pointless: the reader was shown what
 * they had already seen. Zone 2 carries a DIFFERENT surface, a photograph, or
 * an animation. Card 7 is the one intentional exception, showing the ledger
 * larger beside the payout animation, because the ledger is the point of it.
 *
 * Zone 4 routes to three other cards rather than closing. Indices (i+1), (i+2)
 * and (i+3) modulo 8, so every card leads somewhere and nothing is orphaned.
 */
function FeatureOverlay({
  feature,
  onClose,
  onOpen,
}: {
  feature: (typeof FEATURES)[number];
  onClose: () => void;
  onOpen: (f: (typeof FEATURES)[number]) => void;
}) {
  const OverlaySurface = feature.overlaySurface ? SURFACES[feature.overlaySurface] : null;
  const Anim = feature.overlayAnim ? ANIMS[feature.overlayAnim] : null;
  const SecondAnim = feature.overlaySecondAnim ? ANIMS[feature.overlaySecondAnim] : null;
  useDismissable(onClose);

  const index = FEATURES.indexOf(feature);
  const related = [1, 2, 3].map((n) => FEATURES[(index + n) % FEATURES.length]);

  // Zone 2 holds whatever is not the closed card's own surface. Card 7 is the
  // exception: `overlaySurface` there is its own ledger, deliberately.
  const visuals: React.ReactNode[] = [];
  if (feature.overlayImg) {
    visuals.push(
      <div key="img" style={{ position: 'relative', minHeight: 300, borderRadius: 14, overflow: 'hidden', border: `1px solid ${W.rule}`, background: '#F2F8F6' }}>
        <Image src={feature.overlayImg.src} alt={feature.overlayImg.alt} fill sizes="440px" style={{ objectFit: 'contain' }} />
      </div>,
    );
  }
  if (OverlaySurface) {
    visuals.push(
      <div key="surface" style={{ minHeight: 300, display: 'flex', borderRadius: 14, overflow: 'hidden', border: `1px solid ${W.rule}`, background: '#fff' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <OverlaySurface />
        </div>
      </div>,
    );
  }
  if (Anim) visuals.push(<Anim key="anim" />);
  if (SecondAnim) visuals.push(<SecondAnim key="anim2" />);

  return (
    <div className="fvw-overlay" role="dialog" aria-modal="true" aria-label={feature.title} onClick={onClose}>
      <div className="fvw-overlay-panel" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="fvw-overlay-close">
          <X size={18} strokeWidth={1.8} />
        </button>

        {/* Zone 1: header */}
        <div className="fvw-overlay-head">
          <div>
            <h2 style={{ ...T.overlayTitle, margin: '0 0 16px' }}>{feature.title}</h2>
            <p style={{ ...T.body, color: W.body, margin: '0 0 24px' }}>{feature.body}</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
              <a href="#walkthrough" onClick={onClose} style={{ ...primaryButton, background: W.teal, color: W.bone }} className="fvw-cta">
                Get started
              </a>
              <a
                href="#walkthrough"
                onClick={onClose}
                style={{ ...secondaryButton, color: W.teal, border: 'none', padding: '12px 4px' }}
                className="fvw-cta-secondary"
              >
                See how it works
              </a>
            </div>
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 14, alignContent: 'center' }}>
            {feature.bullets.map((b) => (
              <li key={b} style={{ display: 'flex', gap: 10, fontSize: 16, color: W.bodyAlt }}>
                <Check size={17} strokeWidth={2.4} color={W.teal} style={{ flex: 'none', marginTop: 2 }} />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Zone 2: imagery. One column when there is a single visual, never an
            empty half. */}
        <div
          className="fvw-overlay-zone2"
          style={{
            gridTemplateColumns: visuals.length > 1 || feature.stat ? '1fr 1fr' : '1fr',
            // Each column takes its own height. Stretching left a large empty
            // band under whichever animation panel sat beside a taller surface.
            alignItems: 'start',
          }}
        >
          {feature.stat ? (
            <div style={{ display: 'grid', gap: 22, alignContent: 'start' }}>
              <div style={{ background: W.obsidian, borderRadius: 14, padding: '26px 28px' }}>
                <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.045em', color: W.ice, lineHeight: 1 }}>
                  {feature.stat.value}
                </div>
                <div style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(244,250,248,0.78)', marginTop: 10 }}>
                  {feature.stat.caption}
                </div>
              </div>
              {visuals.slice(1)}
            </div>
          ) : null}
          {feature.stat ? visuals.slice(0, 1) : visuals}
        </div>

        {/* Zone 3: three sentences. No headings, no bullets. */}
        <div className="fvw-overlay-zone3">
          {feature.points.map((point) => {
            const PointIconGlyph = POINT_ICONS[point.icon];
            return (
              <div key={point.text} style={{ display: 'flex', gap: 12 }}>
                <span
                  aria-hidden
                  style={{
                    width: 34, height: 34, flex: 'none', borderRadius: 9,
                    border: `1px solid ${point.soon ? W.rule : W.borderStrong}`,
                    background: point.soon ? '#F4F7F6' : '#EFF6F3',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <PointIconGlyph size={16} strokeWidth={1.9} color={point.soon ? W.mute : W.teal} />
                </span>
                <span style={{ ...T.body, color: point.soon ? W.mute : W.bodyAlt }}>
                  {point.text}
                  {/* The WHOLE point reads as pending, rather than a full-strength
                      sentence with a badge stuck mid-air beside it. */}
                  {point.soon && (
                    <span
                      style={{
                        display: 'inline-block', marginTop: 8, fontSize: 12.5, fontWeight: 700,
                        letterSpacing: '0.06em', textTransform: 'uppercase', color: W.teal,
                        background: 'rgba(0,109,109,0.10)', border: '1px solid rgba(0,109,109,0.28)',
                        borderRadius: 5, padding: '3px 9px',
                      }}
                    >
                      Coming soon
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {/* Zone 4: related. Routes to another card rather than closing, so the
            overlay is a place you can move around in. */}
        <div style={{ padding: '44px 60px 0' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>More of the platform</h3>
        </div>
        <div className="fvw-overlay-zone4" style={{ paddingBottom: 50 }}>
          {related.map((r) => {
            const RelatedSurface = SURFACES[r.surface];
            return (
              <button key={r.title} type="button" className="fvw-related" onClick={() => onOpen(r)}>
                <span className="fvw-related-thumb" aria-hidden>
                  <span className="fvw-related-scale">
                    <RelatedSurface />
                  </span>
                </span>
                {/* Two lines whatever the title's length, so the link below sits
                    at the same height on all three cards. */}
                <span
                  style={{
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', fontSize: 15, fontWeight: 600, lineHeight: 1.3,
                    padding: '12px 14px 0', minHeight: 39,
                  }}
                >
                  {r.title}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, color: W.teal, padding: '8px 14px 14px', marginTop: 'auto' }}>
                  Take a look
                  <ChevronRight size={15} strokeWidth={2} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Sections ─────────────────────────────────────────────────────────── */

/**
 * Organiser logos, in knockout form.
 *
 * Wordmarks rather than image files: these are small brands whose logos we do
 * not have in a knockout SVG, and setting them in the page's own typeface reads
 * as deliberate where three mismatched PNGs would read as clip art. Swap in
 * real marks when they are supplied.
 */
/**
 * Real organiser marks, sized generously.
 *
 * The handoff is explicit that these are small brands and that shrinking them
 * defeats the purpose, so est.28 sits at 26px where the other two get 72px:
 * it is a wordmark and the others are stacked lockups, so matching their pixel
 * heights would make it the loudest thing on the strip.
 *
 * `brightness(0) invert(1)` knocks them out to white. The supplied files are
 * full-colour, and three different-coloured logos on a dark strip reads as clip
 * art; one knockout row reads as a customer list.
 */
const ORGANISERS = [
  { name: 'est.28', src: '/website/brand/logo-est28-trim.png', height: 26 },
  { name: 'Strive Fitness', src: '/website/brand/logo-strive.png', height: 72 },
  { name: 'DQ Coaching', src: '/website/brand/logo-dq.png', height: 72 },
];

function LogoStrip() {
  return (
    <div style={{ padding: '52px 72px 96px', textAlign: 'center' }}>
      <div style={{ ...T.small, color: W.dim, marginBottom: 26 }}>Running on Fitvent</div>
      <div className="fvw-logos" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 44, flexWrap: 'wrap' }}>
        {ORGANISERS.map((o) => (
          <Image
            key={o.name}
            src={o.src}
            alt={o.name}
            width={o.height * 5}
            height={o.height}
            sizes="200px"
            // height: auto alongside the explicit height keeps next/image happy
            // about the aspect ratio; the rendered height comes from the wrapper.
            style={{
              height: o.height,
              width: 'auto',
              maxWidth: 200,
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
              opacity: 0.92,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A feature card: copy on a gradient ribbon, with the product surface bleeding
 * off the bottom.
 *
 * The image is the card. An earlier pass shipped these as title + body + ribbon
 * and the grid read as a list of claims with nothing behind them; the whole
 * argument of this section is that the software exists and looks like this.
 *
 * The surface is a component (surfaces.tsx), fluid down to ~340px, so it fills
 * whatever width the card's span gives it instead of being cropped to fit.
 */
function FeatureCard({ feature, onOpen }: { feature: (typeof FEATURES)[number]; onOpen: () => void }) {
  const Surface = SURFACES[feature.surface];
  // On a phone the frame FILLS whatever the card has left. Capped and pushed
  // to the foot with margin-top:auto, every card shorter than the tallest
  // showed the difference as bare gradient, over 300px of it on some.
  const isMobile = useIsMobile();
  return (
    <article
      className="fvw-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        // The card's width IS the design's rhythm: 4+2, 3+3, 2+4, 3+3 across a
        // six-column grid. This was defined in features.ts from the start and
        // never actually applied, so all eight cards sat one-column-wide and
        // six of them crushed into a single row. Everything that looked wrong
        // about this grid was downstream of this one missing line.
        gridColumn: `span ${feature.span}`,
        // A FIXED height on mobile, not a minimum. Without a constraint there
        // is no free-space deficit, so the frame never shrinks and sits at its
        // content height (580 to 680px) no matter what its flex-basis says.
        // One height for the carousel, set here rather than by whichever card
        // happens to carry the tallest surface. 690 is the smallest that fits
        // every surface whole once each has been trimmed for a phone: nothing
        // is cropped mid-row, and the brand card still shows the whole device
        // from its header through Get Tickets.
        ...(isMobile ? { height: 690 } : null),
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 14,
        padding: '34px 34px 0',
        overflow: 'hidden',
        background: feature.ribbon,
        border: `1px solid ${W.rule}`,
        cursor: 'pointer',
      }}
    >
      {/* Expand control. 36px rounded square, top right, per the handoff. */}
      <span aria-hidden className="fvw-expand">
        <ArrowUpRight size={17} strokeWidth={2} />
      </span>

      <h3 className="fvw-card-title" style={{ ...T.cardTitle, margin: '0 0 12px', maxWidth: feature.titleWidth, paddingRight: 44 }}>
        {feature.title}
      </h3>
      <p className="fvw-card-body" style={{ ...T.body, color: W.body, margin: 0, maxWidth: feature.titleWidth }}>
        {feature.body}
      </p>

      {/* The live strip. Card 1 has none: its four phones cross-fade instead. */}
      {feature.band ? <MotionBand id={feature.band} /> : <span style={{ height: 26 }} />}

      {/* The frame bleeds off the bottom: no bottom border, no bottom radius.
          margin-top: auto pushes it down so cards sharing a row line their
          frames up even when their copy runs to different lengths. */}
      <div
        className={feature.devices ? 'fvw-frame fvw-frame-devices' : 'fvw-frame'}
        style={{
          position: 'relative',
          // An auto top margin absorbs the free space BEFORE flex-grow is
          // considered, so on mobile it ate exactly the space the frame was
          // supposed to grow into and left it as bare gradient. Desktop still
          // wants it: there the frame is a fixed height and this is what lines
          // up two frames sitting in the same row.
          marginTop: isMobile ? 0 : 'auto',
          // A minimum, not a fixed height. Now that row-mates are the same
          // height, a card pairing a short frame with a tall one left a huge
          // band of bare gradient under its copy. Letting the frame grow into
          // the slack means the product surface always reaches the bottom edge
          // and the two frames in a row line up, without hand-tuning a number
          // per card every time the copy length changes.
          // flex-basis 0 on mobile, so the frame takes its SHARE of the card
          // rather than being sized by the surface inside it. At basis auto it
          // was as tall as its content (580 to 680px) and every card ran past
          // the screen; the card sets the height and the surface bleeds.
          ...(isMobile ? { flex: '1 1 0%', minHeight: 0 } : { flex: '1 0 auto', minHeight: feature.frame }),
          borderRadius: '14px 14px 0 0',
          borderTop: feature.devices ? 'none' : `1px solid ${W.rule}`,
          borderLeft: feature.devices ? 'none' : `1px solid ${W.rule}`,
          borderRight: feature.devices ? 'none' : `1px solid ${W.rule}`,
          borderBottom: 'none',
          // The devices frame must NOT clip. On every other card the frame is a
          // visible white box and its overflow is what rounds the product
          // surface into the top corners, so it stays hidden there. On the
          // devices card the frame is transparent and borderless, so the clip
          // draws nothing and only takes things away: the phones sit flush on
          // its bottom edge with 9.5px of headroom above them, and the hover
          // lift (translateY(-8px) scale(1.03)) puts the top of the device 6.1px
          // past that edge, which sliced the rounded corners and the top of the
          // header off with a straight horizontal line. Measured on the live
          // page, not guessed. The card's own overflow sits 1px lower and keeps
          // the intended bottom bleed, so nothing else changes.
          overflow: feature.devices ? 'visible' : 'hidden',
          background: feature.devices ? 'transparent' : '#FFFFFF',
        }}
      >
        <Surface />
      </div>
    </article>
  );
}

/**
 * The core panel and the hinge below it.
 *
 * This is the argument of the whole section and a first pass reduced it to four
 * words on a white card, which threw the argument away. The handoff is specific:
 * a DARK panel listing eight capabilities in a four-column grid, each an icon
 * plus a name and a qualifier, headed "The core" / "Identical for every event".
 *
 * The hinge underneath, a stem into a teal circle and out again into "A
 * competition engine for your event type", is what makes the point visually:
 * everything above is the same for everybody, everything below changes by sport.
 * Without it the two halves read as two unrelated sections.
 */
const CORE: Array<[string, string, LucideIcon]> = [
  ['Tickets and checkout', 'Wallets, cards and invoices', Ticket],
  ['Teams and split payments', 'Squads, pairs, clubs, corporates', Users],
  ['Waivers and screening', 'E-signed, stored, exportable', ShieldCheck],
  ['Comms and CRM', 'Automated emails and campaigns', Send],
  ['Photos and add-ons', 'Sold in the same basket', Camera],
  ['Volunteers and officials', 'Rostered, briefed, reminded', HandHeart],
  ['Payouts', 'Withdraw to your own account, any time', Landmark],
  ['Athlete accounts', 'One profile across organisers', UserRound],
];

function CorePanel() {
  return (
    <>
      <div style={{ background: W.obsidian, borderRadius: 20, padding: '36px 40px 40px' }}>
        <div className="fvw-core-head" style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 28 }}>
          <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.03em', color: W.bone }}>The core</span>
          <span style={{ fontSize: 16, color: W.dim }}>Identical for every event</span>
        </div>
        <div className="fvw-core" data-fv-motion>
          {CORE.map(([name, qualifier, CoreIcon], k) => (
            <div
              key={name}
              style={{
                display: 'flex',
                gap: 12,
                // 90ms apart: the eight rows read as one thing assembling
                // rather than eight things arriving.
                animation: `fvLine 620ms cubic-bezier(0.22,1,0.36,1) ${(k * 0.09).toFixed(2)}s both`,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 30, height: 30, flex: 'none', borderRadius: 8,
                  background: 'rgba(42,223,223,0.12)', border: '1px solid rgba(42,223,223,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <CoreIcon size={15} strokeWidth={1.9} color={W.ice} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: W.bone, letterSpacing: '-0.01em' }}>
                  {name}
                </span>
                <span style={{ display: 'block', fontSize: 15, color: W.dim, marginTop: 2 }}>{qualifier}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* The hinge, and it is the argument of the section told in motion:
          the core assembles, then a plus, then your engine. Without the
          sequence the two halves read as two unrelated sections. */}
      <div
        data-fv-motion
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0 30px' }}
      >
        <span
          style={{
            width: 2, height: 30, background: '#C4D8D1', transformOrigin: 'top center',
            animation: 'fvStem 700ms cubic-bezier(0.22,1,0.36,1) both',
          }}
        />
        <span
          aria-hidden
          style={{
            width: 46, height: 46, borderRadius: '50%', background: W.teal,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '6px 0',
            animation: 'fvPop 620ms cubic-bezier(0.22,1,0.36,1) 620ms both',
          }}
        >
          <Plus size={22} strokeWidth={2.4} color="#fff" />
        </span>
        <span
          style={{
            width: 2, height: 30, background: '#C4D8D1', transformOrigin: 'top center',
            animation: 'fvStem 700ms cubic-bezier(0.22,1,0.36,1) 1.1s both',
          }}
        />
        {/* One continuous run of text. As two flex children it wrapped into
            "A competition / engine" beside "for your event / type", which is
            not a sentence anybody can read. */}
        <span
          className="fvw-engine-line"
          style={{
            display: 'inline-block', marginTop: 16,
            background: W.obsidian, color: W.bone, borderRadius: 99,
            padding: '11px 24px', fontSize: 21, fontWeight: 700, letterSpacing: '-0.025em',
          }}
        >
          A competition engine <span style={{ color: W.ice }}>for your event type</span>
        </span>
      </div>
    </>
  );
}

/**
 * The twelve event-type tiles, and the layer one opens.
 *
 * BLACK AND WHITE, deliberately. Twelve colour photographs in a row fight each
 * other and fight the brand; desaturated they read as one set, and the only
 * colour left in the strip is the teal Live chip, which is where the eye should
 * go. Every photo was checked by eye before assignment: the filenames lie, and
 * a show-jumping photograph called sport-climbing.jpg is how a horse came to be
 * filed under Climbing.
 *
 * The tile is a SUMMARY and the layer is the answer. An organiser scanning this
 * strip is asking one question, "is my sport in here", and the tile can only
 * show four formats. The layer shows all of them, plus how the engine actually
 * runs, which is the part that differs by sport and the reason the section
 * exists at all. Data lives in event-types.ts.
 */
function EventTypeScroller({ onOpen }: { onOpen: (t: EventType) => void }) {
  return (
    <>
      <div className="fvw-scroller">
        {EVENT_TYPES.map((t) => {
          const shown = t.formats.slice(0, CHIPS_ON_TILE);
          const rest = t.formats.length - shown.length;
          const Icon = t.icon;
          return (
            <button
              key={t.name}
              type="button"
              className="fvw-tile"
              onClick={() => onOpen(t)}
              aria-label={`${t.name}: ${t.formats.length} formats`}
            >
              <Image src={t.img} alt="" fill sizes="300px" className="fvw-tile-img" style={{ objectFit: 'cover' }} />
              <div className="fvw-tile-scrim" aria-hidden />
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'auto' }}>
                  <span
                    aria-hidden
                    style={{
                      width: 32, height: 32, flex: 'none', borderRadius: 9,
                      background: 'rgba(12,19,18,0.55)', border: '1px solid rgba(244,250,248,0.22)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Icon size={16} strokeWidth={1.9} color={t.live ? W.ice : 'rgba(244,250,248,0.82)'} />
                  </span>
                  <span
                    style={{
                      fontSize: 13, fontWeight: 600, padding: '3px 9px', borderRadius: 5,
                      background: t.live ? W.teal : 'rgba(244,250,248,0.16)',
                      color: t.live ? '#fff' : 'rgba(244,250,248,0.88)',
                    }}
                  >
                    {t.live ? 'Live' : 'Coming soon'}
                  </span>
                </div>

                <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: W.bone, marginBottom: 5 }}>
                  {t.name}
                </div>
                {/* Fixed blocks, so twelve tiles line up. Without a min-height a
                    tile whose copy fits on one line sits its chips 30px above
                    its neighbour's, which twelve in a row makes obvious. */}
                <div style={{ fontSize: 15, color: 'rgba(244,250,248,0.76)', lineHeight: 1.45, marginBottom: 12, minHeight: 44 }}>
                  {t.engine}
                </div>
                <div style={{ display: 'flex', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                  {shown.map((f) => (
                    <span
                      key={f}
                      style={{
                        fontSize: 13, padding: '3px 8px', borderRadius: 5,
                        background: 'rgba(244,250,248,0.14)', color: 'rgba(244,250,248,0.9)',
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
                {/* The count sits on its own line under a SINGLE row of chips.
                    Wrapping to a second row pushed this line out of alignment
                    with the neighbouring tiles, which is what made the strip
                    look ragged. */}
                <div style={{ fontSize: 13, color: 'rgba(244,250,248,0.62)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {rest > 0 ? `+${rest} more` : 'See how it runs'}
                  <ArrowUpRight size={13} strokeWidth={2} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * The layer behind a tile: what this event type actually gets.
 *
 * Formats for the two live types are the templates that really ship, not
 * marketing names (see event-types.ts). For the ten that are coming, the layer
 * repeats "Coming soon" rather than letting a confident-looking list imply
 * otherwise.
 */
function EventTypeOverlay({ type, onClose }: { type: EventType; onClose: () => void }) {
  const Icon = type.icon;
  useDismissable(onClose);
  return (
    <div className="fvw-overlay" role="dialog" aria-modal="true" aria-label={type.name} onClick={onClose}>
      <div className="fvw-overlay-panel" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="fvw-overlay-close">
          <X size={18} strokeWidth={1.8} />
        </button>

        {/* Header, on the type's own photograph. */}
        <div className="fvw-type-header" style={{ position: 'relative', height: 168, overflow: 'hidden', background: W.obsidian }}>
          <Image
            src={type.img}
            alt=""
            fill
            sizes="900px"
            style={{ objectFit: 'cover', filter: 'grayscale(1) contrast(0.92) brightness(1.5)', opacity: 0.55 }}
          />
          {/* Teal, not more black. The photograph is already monochrome and the
              panel behind it is dark, so a black scrim made three dark layers
              and the header disappeared into the section behind it. */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(10,42,42,0.30) 0%, rgba(10,32,30,0.72) 55%, rgba(12,19,18,0.94) 100%)',
            }}
          />
          <div className="fvw-type-head" style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'flex-end', gap: 14, padding: '0 40px 22px' }}>
            <span
              aria-hidden
              style={{
                width: 44, height: 44, flex: 'none', borderRadius: 11,
                background: 'rgba(12,19,18,0.6)', border: '1px solid rgba(244,250,248,0.24)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon size={22} strokeWidth={1.8} color={type.live ? W.ice : W.bone} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', color: W.bone }}>
                {type.name}
              </span>
              <span style={{ display: 'block', fontSize: 16, color: 'rgba(244,250,248,0.76)', marginTop: 2 }}>
                {type.engine}
              </span>
            </span>
            <span
              style={{
                marginLeft: 'auto', flex: 'none', fontSize: 13, fontWeight: 600,
                padding: '5px 11px', borderRadius: 6,
                background: type.live ? W.teal : 'rgba(244,250,248,0.18)', color: '#fff',
              }}
            >
              {type.live ? 'Live' : 'Coming soon'}
            </span>
          </div>
        </div>

        <div style={{ padding: '30px 40px 40px' }}>
          <div className="fvw-overlay-cols">
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
                How it runs
              </h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
                {type.how.map((line) => (
                  <li key={line} style={{ display: 'flex', gap: 10, fontSize: 15.5, lineHeight: 1.55, color: W.body }}>
                    <span
                      aria-hidden
                      style={{
                        width: 22, height: 22, flex: 'none', marginTop: 1, borderRadius: 6,
                        background: 'rgba(0,109,109,0.10)', border: '1px solid rgba(0,109,109,0.22)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Check size={12} strokeWidth={2.4} color={W.teal} />
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
                Formats <span style={{ color: W.mute, fontWeight: 400 }}>({type.formats.length})</span>
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {type.formats.map((f) => (
                  <span
                    key={f}
                    style={{
                      fontSize: 14.5, padding: '6px 12px', borderRadius: 7,
                      background: '#FFFFFF', border: `1px solid ${W.rule}`, color: W.ink,
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
              <p style={{ fontSize: 14.5, color: W.muteL, margin: '16px 0 0', lineHeight: 1.55 }}>
                {type.live
                  ? 'Everything in the core comes with it: tickets, teams, waivers, comms, volunteers and payouts.'
                  : 'The core is already built for this. What is still coming is the competition engine above.'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 30 }}>
            {/* Both land on the contact form. "Tell us you run this" used to go
                to /sign-up, which is a strange answer to somebody telling you
                their sport is not supported yet. */}
            <a href="#walkthrough" onClick={onClose} style={{ ...primaryButton, background: W.teal, color: '#fff' }} className="fvw-cta">
              {type.live ? 'Get started' : 'Tell us you run this'}
            </a>
            <a href="#walkthrough" onClick={onClose} style={{ ...secondaryButton, color: W.ink, borderColor: W.borderStrong }} className="fvw-cta-secondary">
              Book a walkthrough
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The emphasised fragments carry the claim; the rest is the sentence around it.
 * A quote set in one even weight is a grey block the eye slides off, and the
 * part worth reading is never the whole of it.
 *
 * STILL TO CONFIRM: these are attributed to two real, photographed people and
 * came in with the design bundle. If they are placeholder copy rather than
 * words those people said, they must be replaced before this goes live.
 */
const TESTIMONIALS: Array<{
  quote: Array<string | { em: string }>;
  name: string;
  role: string;
  portrait: string;
}> = [
  {
    quote: [
      'For years I ran events on a leading ticketing platform that was ',
      { em: 'never built for fitness' },
      '. Fitvent carried our brand end to end and ',
      { em: 'cut the admin right down' },
      '.',
    ],
    name: 'David Quirke',
    role: 'Owner, DQ Coaching',
    portrait: '/website/uploads/david-quirke-portrait.jpg',
  },
  {
    quote: [
      'We were ',
      { em: 'live and selling in about fifteen minutes' },
      ', on a page that looked like Strive, and our athletes kept messaging to say ',
      { em: 'how slick it was' },
      '.',
    ],
    name: 'DJ Canning',
    role: 'Owner, Strive Fitness',
    portrait: '/website/uploads/dj-canning-portrait.jpg',
  },
];

const INCLUDED = [
  'Every live format',
  'Waves, heats and live scoring',
  'Team and split payments',
  'Photos, merch and add-ons',
  'Volunteers and officials',
  'Withdraw to your bank any time',
];

const footerLink: React.CSSProperties = { color: '#7C8D88', textDecoration: 'none' };
